import { useLayoutEffect, useRef, useState } from 'react';

const pathUsers = new WeakMap();
const viewportSelector = '.ant-modal-body, .ant-drawer-body, .global-settings-content-body, .hrm-page';

export function tableBodyHeight({ available, header = 0, pagination = 0, title = 0, footer = 0, scrollbar = 0 }) {
  return Math.max(0, Math.floor(available - header - pagination - title - footer - scrollbar));
}

// Only the ancestors of opted-in tables become fill panels. Sibling toolbars,
// forms, tab bars and pagination remain in flow and consume real space. Multiple
// tables in a pane share that space instead of each claiming a viewport height.
function fillPath(root) {
  const viewport = root.closest(viewportSelector);
  if (!viewport) return () => {};
  // HRM's route has an auto-height legacy content wrapper. Fill its real shell
  // content area too; stopping at .hrm-page leaves no height for table rows.
  const boundary = viewport.matches('.hrm-page') ? viewport.closest('.ant-layout-content') || viewport : viewport;
  const nodes = [];
  for (let node = root.parentElement; node; node = node.parentElement) {
    if (window.getComputedStyle(node).display === 'contents') continue;
    const current = pathUsers.get(node);
    if (current) current.count += 1;
    else {
      const grid = window.getComputedStyle(node).display === 'grid';
      pathUsers.set(node, { count: 1, grid });
      node.classList.add('srm-table-fill-path');
      if (grid) node.classList.add('srm-table-fill-grid');
    }
    nodes.push(node);
    if (node === boundary) break;
  }
  const dialog = viewport.matches('.ant-modal-body') ? viewport.closest('.ant-modal-content') : null;
  if (dialog) {
    const current = pathUsers.get(dialog);
    if (current) current.count += 1;
    else { pathUsers.set(dialog, { count: 1, dialog: true }); dialog.classList.add('srm-table-dialog-frame'); }
    nodes.push(dialog);
  }
  return () => nodes.forEach(node => {
    const current = pathUsers.get(node);
    if (!current || --current.count > 0) return;
    node.classList.remove('srm-table-fill-path');
    if (current.grid) node.classList.remove('srm-table-fill-grid');
    if (current.dialog) { node.classList.remove('srm-table-dialog-frame'); node.style.removeProperty('--srm-table-dialog-height'); }
    pathUsers.delete(node);
  });
}

const outerHeight = element => {
  if (!element) return 0;
  const style = window.getComputedStyle(element);
  return element.getBoundingClientRect().height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
};

export function useTableViewport(enabled = true) {
  const tableRef = useRef(null), [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const root = tableRef.current;
    if (!enabled || !root) return undefined;
    const release = fillPath(root);
    let frame, stopped = false;
    const schedule = () => { if (!stopped && frame == null) frame = requestAnimationFrame(() => { frame = null; measure(); }); };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    const measure = () => {
      if (stopped) return;
      const dialog = root.closest('.srm-table-dialog-frame');
      if (dialog) {
        const frame = dialog.closest('.ant-modal');
        const style = frame && window.getComputedStyle(frame);
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const available = viewportHeight - (parseFloat(style?.top) || 0) - (parseFloat(style?.paddingBottom) || 0);
        // Bound the dialog, not its rows. Its real header/footer and form fields
        // stay in flow; the same table measurement below uses the remainder.
        dialog.style.setProperty('--srm-table-dialog-height', `${Math.max(0, Math.min(viewportHeight * 0.8, available))}px`);
      }
      if (!root.clientHeight) return;
      const header = root.querySelector('.ant-table-header') || root.querySelector('.ant-table-thead');
      const pagination = root.querySelector('.ant-pagination');
      const title = root.querySelector('.ant-table-title');
      const footer = root.querySelector('.ant-table-footer');
      const scrollbar = root.querySelector('.ant-table-sticky-scroll');
      [header, pagination, title, footer, scrollbar].filter(Boolean).forEach(node => resize?.observe(node));
      const next = tableBodyHeight({ available: root.clientHeight, header: outerHeight(header), pagination: outerHeight(pagination), title: outerHeight(title), footer: outerHeight(footer), scrollbar: outerHeight(scrollbar) });
      setHeight(previous => previous === next ? previous : next);
    };
    resize?.observe(root);
    const mutations = new MutationObserver(schedule);
    mutations.observe(root, { childList: true, subtree: true, characterData: true });
    window.addEventListener('resize', schedule);
    document.fonts?.addEventListener?.('loadingdone', schedule);
    measure();
    return () => {
      stopped = true;
      if (frame != null) cancelAnimationFrame(frame);
      resize?.disconnect(); mutations.disconnect(); release();
      window.removeEventListener('resize', schedule);
      document.fonts?.removeEventListener?.('loadingdone', schedule);
    };
  }, [enabled]);
  return { tableRef, height };
}
